import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { GLTFLoader, GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { CanvasContext, CardRegistration } from "./CanvasContext";
import { brickModelPaths } from "../../utils/brickModelPaths";

interface CardData {
  id: string;
  modelPath: string;
  element: HTMLElement;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  viewport: { x: number; y: number; width: number; height: number };
  width: number;
  height: number;
  modelGroup: THREE.Group | null;
}

interface GlobalCanvasManagerProps {
  children: React.ReactNode;
}

const GlobalCanvasManager: React.FC<GlobalCanvasManagerProps> = ({
  children,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cardsRef = useRef<Map<string, CardData>>(new Map());
  const animationFrameRef = useRef<number>();
  const loaderRef = useRef<GLTFLoader>(new GLTFLoader());
  // Model cache: stores pre-loaded GLTF models for instant cloning
  const modelCacheRef = useRef<Map<string, GLTF>>(new Map());
  // Every card that has registered, whether or not a renderer exists yet.
  // Cards can mount before the renderer initializes, so this is the source of
  // truth used to (re)build cardsRef once a renderer is available.
  const registrationsRef = useRef<Map<string, CardRegistration>>(new Map());
  const [isInitialized, setIsInitialized] = useState(false);
  const [modelsPreloaded, setModelsPreloaded] = useState(false);
  // True when the browser refused to give us a WebGL context at all
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  // RAF throttling for scroll events
  const pendingScrollSyncRef = useRef<boolean>(false);

  // Pre-load all GLB models into cache at startup
  useEffect(() => {
    const preloadModels = async () => {
      const loader = loaderRef.current;
      const modelPaths = Object.values(brickModelPaths);
      const uniquePaths = [...new Set(modelPaths)]; // Remove duplicates

      let loadedCount = 0;

      // Load all models in parallel (browser will handle concurrency)
      const loadPromises = uniquePaths.map((path) => {
        return new Promise<void>((resolve) => {
          loader.load(
            path,
            (gltf) => {
              modelCacheRef.current.set(path, gltf);
              loadedCount++;
              if (
                loadedCount % 10 === 0 ||
                loadedCount === uniquePaths.length
              ) {
              }
              resolve();
            },
            undefined,
            (error) => {
              console.error(
                `[GlobalCanvasManager] Failed to pre-load ${path}:`,
                error
              );
              resolve(); // Continue even if one fails
            }
          );
        });
      });

      await Promise.all(loadPromises);
      setModelsPreloaded(true);
    };

    preloadModels();
  }, []);

  // Initialize Three.js renderer
  useEffect(() => {
    if (!canvasRef.current) return;

    // The browser can refuse to create a WebGL context entirely (no GPU, driver
    // blocklist, hardware acceleration disabled, remote session). Three.js
    // throws in that case, which would take down the whole React tree from
    // inside this effect. Degrade to a 2D game instead of a blank page.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas: canvasRef.current,
        antialias: true,
        alpha: true,
      });
    } catch (error) {
      console.error(
        "[GlobalCanvasManager] WebGL is unavailable; 3D models will not render.",
        error
      );
      setWebglUnavailable(true);
      return;
    }

    renderer.setSize(
      document.documentElement.clientWidth,
      document.documentElement.clientHeight
    );
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setScissorTest(true);
    // Set clear color to fully transparent to prevent black squares
    renderer.setClearColor(0x000000, 0);
    // Disable autoClear so we can manually clear each viewport
    renderer.autoClear = false;

    rendererRef.current = renderer;
    setIsInitialized(true);

    return () => {
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []);

  // Sync viewport positions for all registered cards
  const syncViewports = useCallback(() => {
    cardsRef.current.forEach((card) => {
      const rect = card.element.getBoundingClientRect();
      // WebGL coordinates: origin at bottom-left, Y increases upward
      // DOM coordinates: origin at top-left, Y increases downward
      // So we need to flip: WebGL_Y = windowHeight - DOM_bottom
      card.viewport = {
        x: Math.floor(rect.left),
        y: Math.floor(
          document.documentElement.clientHeight - rect.top - rect.height
        ),
        width: Math.floor(rect.width),
        height: Math.floor(rect.height),
      };
    });
  }, []);

  // Load GLB model and add to scene (with cache support)
  const loadModel = useCallback((card: CardData) => {
    // Check if model is in cache
    const cachedModel = modelCacheRef.current.get(card.modelPath);

    if (cachedModel) {
      // INSTANT: Clone from cache (no file loading/parsing needed)
      const clone = cachedModel.scene.clone(true);

      const group = new THREE.Group();
      group.add(clone);
      group.scale.set(1.2, 1.2, 1.2);

      card.modelGroup = group;
      card.scene.add(group);
    } else {
      // Fallback: Load from file if not in cache yet
      loaderRef.current.load(
        card.modelPath,
        (gltf) => {
          // Add to cache for future use
          modelCacheRef.current.set(card.modelPath, gltf);

          // Clone the loaded scene
          const clone = gltf.scene.clone(true);

          // Create a group for rotation
          const group = new THREE.Group();
          group.add(clone);
          group.scale.set(1.2, 1.2, 1.2);

          card.modelGroup = group;
          card.scene.add(group);
        },
        undefined,
        (error) => {
          console.error(
            `[GlobalCanvasManager] Error loading model ${card.modelPath}:`,
            error
          );
        }
      );
    }
  }, []);

  // Build the Three.js objects for a registration. Only called once a renderer
  // exists.
  const createCard = useCallback(
    (registration: CardRegistration) => {
      // Create scene and camera for this card
      const scene = new THREE.Scene();

      const camera = new THREE.PerspectiveCamera(
        30,
        registration.width / registration.height,
        0.1,
        500
      );
      camera.position.set(2, 2, 6);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 0, 0); // Point camera at origin where model will be

      // Add lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 5.0);
      directionalLight.position.set(2, 2, 5);
      scene.add(directionalLight);

      // Calculate correct viewport position immediately to prevent flash at (0,0)
      const rect = registration.element.getBoundingClientRect();
      const viewport = {
        x: Math.floor(rect.left),
        y: Math.floor(
          document.documentElement.clientHeight - rect.top - rect.height
        ),
        width: Math.floor(rect.width),
        height: Math.floor(rect.height),
      };

      const cardData: CardData = {
        id: registration.id,
        modelPath: registration.modelPath,
        element: registration.element,
        scene,
        camera,
        viewport, // Use calculated viewport instead of (0, 0)
        width: registration.width,
        height: registration.height,
        modelGroup: null,
      };

      // Load the model
      loadModel(cardData);

      cardsRef.current.set(registration.id, cardData);
      // Schedule viewport sync for next frame (in case layout changes)
      pendingScrollSyncRef.current = true;
    },
    [loadModel]
  );

  // Register a new card
  const registerCard = useCallback(
    (registration: CardRegistration) => {
      // Always retain the registration. Cards register on mount with a
      // [brickId] dependency, so they never re-register - dropping them here
      // while the renderer is still initializing would lose them permanently.
      registrationsRef.current.set(registration.id, registration);

      if (isInitialized) {
        createCard(registration);
      }
    },
    [isInitialized, createCard]
  );

  // Build cards for any registrations that arrived before the renderer existed
  useEffect(() => {
    if (!isInitialized) return;

    registrationsRef.current.forEach((registration, id) => {
      if (!cardsRef.current.has(id)) {
        createCard(registration);
      }
    });
  }, [isInitialized, createCard]);

  // Unregister a card
  const unregisterCard = useCallback((id: string) => {
    registrationsRef.current.delete(id);

    const card = cardsRef.current.get(id);
    if (card && rendererRef.current) {
      // Clear the viewport region before removing the card
      const { x, y, width, height } = card.viewport;
      if (width > 0 && height > 0) {
        rendererRef.current.setViewport(x, y, width, height);
        rendererRef.current.setScissor(x, y, width, height);
        rendererRef.current.clear();
      }

      // Cleanup Three.js objects
      if (card.modelGroup) {
        card.scene.remove(card.modelGroup);
      }
      cardsRef.current.delete(id);

      // Trigger viewport sync on next frame to update remaining cards
      pendingScrollSyncRef.current = true;
    }
  }, []);

  // Trigger viewport sync on next frame (for drag-and-drop, etc.)
  const triggerSync = useCallback(() => {
    pendingScrollSyncRef.current = true;
  }, []);

  // Check if element is visible and within scrollable container bounds
  const isElementVisible = useCallback((element: HTMLElement): boolean => {
    // Check display/visibility styles
    let current: HTMLElement | null = element;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (style.visibility === "hidden" || style.display === "none") {
        return false;
      }
      current = current.parentElement;
    }

    // Check if element is within scrollable container bounds
    const rect = element.getBoundingClientRect();

    // Find scrollable parent container
    let scrollParent: HTMLElement | null = element.parentElement;
    while (scrollParent && scrollParent !== document.body) {
      const style = window.getComputedStyle(scrollParent);
      const hasOverflow =
        style.overflowY === "auto" || style.overflowY === "scroll";

      if (hasOverflow) {
        // Check if element is completely outside scrollable parent's visible bounds
        const parentRect = scrollParent.getBoundingClientRect();

        // Element is completely outside if:
        // - Bottom is above parent's top
        // - Top is below parent's bottom
        // - Right is left of parent's left
        // - Left is right of parent's right
        if (
          rect.bottom < parentRect.top ||
          rect.top > parentRect.bottom ||
          rect.right < parentRect.left ||
          rect.left > parentRect.right
        ) {
          return false;
        }

        // Found scrollable parent and element is within bounds, stop checking
        break;
      }

      scrollParent = scrollParent.parentElement;
    }

    return true;
  }, []);

  // Animation loop
  useEffect(() => {
    if (!rendererRef.current || !isInitialized) return;

    const renderer = rendererRef.current;
    const spinSpeed = 0.003; // Reduced from 0.01 for slower rotation

    // Ensure first frame syncs viewports
    pendingScrollSyncRef.current = true;

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // Only sync viewports when needed (on scroll, resize, or first frame)
      if (pendingScrollSyncRef.current) {
        syncViewports();
        pendingScrollSyncRef.current = false;
      }

      cardsRef.current.forEach((card) => {
        // Check if card is visible
        if (!isElementVisible(card.element)) return;

        // Update rotation
        if (card.modelGroup) {
          card.modelGroup.rotation.y += spinSpeed;
        }

        // Update camera aspect ratio
        const { x, y, width, height } = card.viewport;
        if (width > 0 && height > 0) {
          card.camera.aspect = width / height;
          card.camera.updateProjectionMatrix();

          // Set viewport and scissor for this card
          renderer.setViewport(x, y, width, height);
          renderer.setScissor(x, y, width, height);

          // Clear only this viewport region
          renderer.clear();

          // Render this card's scene
          renderer.render(card.scene, card.camera);
        }
      });
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isInitialized, syncViewports, isElementVisible]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (rendererRef.current) {
        rendererRef.current.setSize(
          document.documentElement.clientWidth,
          document.documentElement.clientHeight
        );
      }
      // Set flag for sync on next frame
      pendingScrollSyncRef.current = true;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle scroll - listen to both window and all scrollable containers
  // Use RAF throttling to prevent layout thrashing
  useEffect(() => {
    const handleScroll = () => {
      // Set flag instead of calling syncViewports directly
      // Animation loop will handle the actual sync once per frame
      pendingScrollSyncRef.current = true;
    };

    // Listen to window scroll
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Find and listen to all scrollable containers
    const scrollableContainers: HTMLElement[] = [];
    const findScrollableContainers = () => {
      const elements = document.querySelectorAll("*");
      elements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const style = window.getComputedStyle(htmlEl);
        if (
          style.overflowY === "auto" ||
          style.overflowY === "scroll" ||
          style.overflowX === "auto" ||
          style.overflowX === "scroll"
        ) {
          scrollableContainers.push(htmlEl);
          htmlEl.addEventListener("scroll", handleScroll, { passive: true });
        }
      });
    };

    // Initial scan for scrollable containers
    findScrollableContainers();

    // Re-scan when DOM changes (for dynamically added containers)
    const observer = new MutationObserver(() => {
      // Remove old listeners
      scrollableContainers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
      scrollableContainers.length = 0;
      // Re-scan
      findScrollableContainers();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      scrollableContainers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
      observer.disconnect();
    };
  }, []);

  return (
    <CanvasContext.Provider
      value={{ registerCard, unregisterCard, syncViewports, triggerSync }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
          opacity: modelsPreloaded && !webglUnavailable ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {children}
    </CanvasContext.Provider>
  );
};

export default GlobalCanvasManager;
